/**
 * The tooltip: a dark label that names what it points at, placed where the
 * caller says — beside the cursor, or beside a control — and kept on screen.
 *
 * Two tones: the default, and `danger`, whose type and ring turn coral (the
 * drag readout's refusal) — the name every coral tone has in this layer. A
 * tooltip holds a few words, not controls; a card with a button in it is a
 * HoverCard. `wide` lets a readout run longer before it wraps: a verdict with
 * its facts under it, as a dragged route's is.
 *
 * At a point (`x`, `y`) it sits diagonally off it, `offset` right and down —
 * or, `side` above, above it and to the right, its foot `offset.y` over the
 * point, for a readout that must leave what is under the pointer in view (a
 * dragged route's over the city it would land on); `beside` an element it
 * sits `side` of it, centred along it, and follows it as the page scrolls or
 * resizes — an icon button's name, which is all it has (IconButton's `tip`).
 * Either way it flips to the other side at the window's edge — the same
 * placement (`placeBeside`) as every floating layer.
 *
 * Beside an element it hangs from that element's control as a popover does
 * (useHang): on the page, with the page — inert with it under a dialog, and
 * drawn under the dialog's scrim; in a dialog, from it, over it; on a rung
 * over the dialogs, on that rung. Drawn on --z-tooltip over everything, the
 * tip IconButton hands the stack outranked a moment that opened while it
 * showed, and the moment had the focus and the keys only once the tip had
 * gone. At a point — the cursor's — it is above the page on --z-tooltip, over
 * everything.
 */

import { useLayoutEffect, useRef } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';

import { placeBeside } from '../../utils/clamp-to-viewport';
import type { Side } from '../../utils/clamp-to-viewport';
import { useHang } from './modal-layers';
import { abovePage } from './page-layers';
import { useAnchoredPosition } from './use-anchored-position';
import styles from './tooltip.module.css';

/** Beside a point — the cursor. */
interface AtPoint {
  /** X position (typically cursor x) */
  x: number;
  /** Y position (typically cursor y) */
  y: number;
  /** Diagonally right and down of the point (the default), or above it and to the right. */
  side?: 'right' | 'above';
  /** Offset from the position in pixels: right, and down — or, above, how far over the point its foot is. */
  offset?: { x?: number; y?: number };
  /** Keeps tooltip on-screen (default: true) */
  constrainToViewport?: boolean;
  /** Minimum padding from screen edge (default: 8) */
  viewportPadding?: number;
  beside?: never;
}

/** Beside an element: the element, and the side the name goes on. */
interface BesideElement {
  beside: { anchor: RefObject<HTMLElement | null>; side: Side };
  /** The label's own element, for a caller that ranks it among the open layers: IconButton hands it to the behaviour hook its Escape goes through. */
  layerRef?: RefObject<HTMLDivElement | null>;
  x?: never;
  y?: never;
  side?: never;
  offset?: never;
  constrainToViewport?: never;
  viewportPadding?: never;
}

interface TooltipBase {
  /** Content to display in the tooltip */
  children: ReactNode;
  /** Whether the tooltip is visible */
  visible: boolean;
  /** `danger` turns the type and ring coral. */
  tone?: 'default' | 'danger';
  /** A readout — a verdict and its facts — that runs longer before it wraps. */
  wide?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Optional inline styles */
  style?: CSSProperties;
}

export type TooltipProps = TooltipBase & (AtPoint | BesideElement);

/**
 * The tone's class, looked up rather than composed: `common-tooltip--${variant}`
 * once resolved to a class no rule defined, and every tooltip rendered in the
 * default tone with nothing reporting it. Typed, so a new tone that forgets its
 * class is a build error.
 */
const TONE: Record<NonNullable<TooltipProps['tone']>, string> = {
  default: '',
  danger: styles.danger,
};

export function Tooltip({ className = '', ...rest }: TooltipProps) {
  return rest.beside ? (
    <BesideTooltip {...rest} beside={rest.beside} className={className} />
  ) : (
    <PointTooltip {...rest} className={className} />
  );
}

/** The label beside an element, centred along it, following it, hanging where its control is (useHang). */
function BesideTooltip({
  children,
  visible,
  beside,
  layerRef,
  tone = 'default',
  wide = false,
  className = '',
  style,
}: TooltipBase & Pick<BesideElement, 'beside' | 'layerRef'>) {
  const ref = useAnchoredPosition<HTMLDivElement>(beside.anchor, beside.side, visible, 'center');
  const hang = useHang();
  if (!visible) return null;
  return hang.portal(
    <div
      ref={(element) => {
        ref.current = element;
        if (layerRef) layerRef.current = element;
        const letGo = hang.from(element);
        return () => {
          ref.current = null;
          if (layerRef) layerRef.current = null;
          letGo?.();
        };
      }}
      className={[styles.tooltip, styles.hanging, TONE[tone], wide ? styles.wide : '', className].filter(Boolean).join(' ')}
      style={{ ...hang.style, ...style }}
      role="tooltip"
    >
      {children}
    </div>,
  );
}

/** The label diagonally off a point — the cursor — flipped at the window's edge. */
function PointTooltip({
  children,
  visible,
  x = 0,
  y = 0,
  side = 'right',
  offset = { x: 16, y: 16 },
  tone = 'default',
  wide = false,
  className = '',
  style,
  constrainToViewport = true,
  viewportPadding = 8,
}: TooltipBase & AtPoint) {
  const offsetX = offset.x ?? 16;
  const offsetY = offset.y ?? 16;
  const posX = x + offsetX;
  const posY = y + offsetY;

  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!visible) return;

    const el = tooltipRef.current;
    if (!el) return;

    // Diagonally off the point — or above it — flipped to its other side at the window's edge.
    const { width, height } = el.getBoundingClientRect();
    const point = { left: x, top: y, right: x, bottom: y };
    const at = !constrainToViewport
      ? { x: posX, y: side === 'above' ? y - offsetY - height : posY }
      : side === 'above'
        ? placeBeside(point, { width, height }, 'above', { gap: offsetY, crossOffset: offsetX, margin: viewportPadding })
        : placeBeside(point, { width, height }, 'right', { gap: offsetX, crossOffset: offsetY, margin: viewportPadding });

    if (style?.left == null) el.style.left = `${at.x}px`;
    if (style?.top == null) el.style.top = `${at.y}px`;
  }, [visible, constrainToViewport, side, posX, posY, offsetX, offsetY, viewportPadding, x, y, children, style?.left, style?.top]);

  if (!visible) {
    return null;
  }

  const node = (
    <div
      ref={tooltipRef}
      className={[styles.tooltip, TONE[tone], wide ? styles.wide : '', className].filter(Boolean).join(' ')}
      style={{
        left: posX,
        top: posY,
        ...style,
      }}
      role="tooltip"
    >
      {children}
    </div>
  );

  // Above the page, so "fixed" positioning is viewport-relative even inside transformed/scroll containers.
  return abovePage(node, 'tooltip');
}
